import { PaymentElement, useElements } from '@stripe/react-stripe-js';
import { ModalButton } from '../../../../SubComponents/Buttons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { graphqlSdk } from '../../../../Utils/graphql-client';
import { Label } from '../../../../SubComponents/Text';
import VoucherWrapper from './voucher-wrapper';
import useFormatPrice from '../../../../Hooks/use-format-price';
import useTypeSafeLocalize from '../../../../Hooks/use-type-safe-localize';
import {
  LocalizationCategory,
  ProductLocalization,
} from '../../../../localizations/types';
import useBookingPrices from '../../../../Hooks/use-booking-prices';
import AdditionalCustomerDataForm from './additional-customer-data-form';
import { LabelWrapper, StyledAddressElement, StyledForm } from './style';
import useHandleBookingPayment from './use-handle-booking-payment';
import { bookingState } from '../../client-state/booking-state-context';
import { customerReducerActionTypes } from '../../../../redusers/customer-reducer';
import useBookingMinimalShop from '../../server-data/use-booking-minimal-shop';

const PaymentForm = () => {
  const localize = useTypeSafeLocalize();
  const elements = useElements();
  const { data: minimalShop } = useBookingMinimalShop();
  const { state, dispatch } = useContext(bookingState);
  const formatPrice = useFormatPrice();
  const { bookingPrices } = useBookingPrices();
  const [showFiscalCodeField, setShowFiscalCodeField] = useState(false);
  const [addressFormComplete, setAddressFormComplete] = useState(false);
  const isShopCountryItaly = useMemo(
    () => minimalShop?.country === 'Italy',
    [minimalShop?.country]
  );

  const isPayButtonDisabled = useMemo(() => {
    // Only check form validity if the shop is in Italy
    if (isShopCountryItaly) {
      let isDisabled = false;

      // addressFormComplete should be true
      if (!addressFormComplete) {
        isDisabled = true;
      }

      // if showFiscalCodeField is true
      // then state.customer.customer.pin should exist and be longer than 3 characters
      if (
        showFiscalCodeField &&
        (!state.customer.customer.pin || state.customer.customer.pin.length <= 3)
      ) {
        isDisabled = true;
      }

      return isDisabled;
    }

    return false;
  }, [
    addressFormComplete,
    isShopCountryItaly,
    showFiscalCodeField,
    state.customer.customer.pin,
  ]);

  useEffect(() => {
    const updatePaymentIntentAmount = async () => {
      if (
        state.booking.id &&
        state.payment.paymentIntentClientSecret &&
        elements !== null
      ) {
        // Sync payment intent amount
        await graphqlSdk.updatePaymentIntentAmount({
          bookingId: state.booking.id,
          paymentIntentId: state.payment.paymentIntentClientSecret.replace(
            /_secret_.+/,
            ''
          ),
        });

        // Fetch payment intent updates
        await elements.fetchUpdates();
      }
    };

    updatePaymentIntentAmount();
  }, [state.booking.id, state.payment.paymentIntentClientSecret, elements]);

  const { handlePayment, errorMessage, loading, pinError, setPinError } =
    useHandleBookingPayment();

  return (
    <StyledForm>
      {isShopCountryItaly ? (
        <StyledAddressElement
          options={{
            mode: 'billing',
            fields: {
              phone: 'never',
            },
            display: {
              name: 'split',
            },
            defaultValues: {
              firstName: state.customer.customer.firstname,
              lastName: state.customer.customer.lastname,
              address: {
                country:
                  state.customer.customer?.address?.country ?? minimalShop?.country ?? '',
              },
            },
          }}
          onChange={(event) => {
            if (event.value.address.country === 'IT') {
              setShowFiscalCodeField(true);
            } else if (event.value.address.country !== 'IT') {
              setShowFiscalCodeField(false);

              // Reset pin when hiding the field
              if (state.customer.customer.pin) {
                dispatch({
                  type: customerReducerActionTypes.UPDATE_CUSTOMER,
                  payload: { ...state.customer.customer, pin: '' },
                });
              }
            }

            if (event.complete) {
              setAddressFormComplete(true);
            } else if (!event.complete) {
              setAddressFormComplete(false);
            }
          }}
        />
      ) : null}

      {isShopCountryItaly && showFiscalCodeField ? (
        <AdditionalCustomerDataForm pinError={pinError} setPinError={setPinError} />
      ) : null}

      <PaymentElement
        options={{
          defaultValues: {
            billingDetails: {
              email: state.customer.customer.email ?? undefined,
              name: `${state.customer.customer.firstname} ${state.customer.customer.lastname}`,
              phone: state.customer.customer.phone ?? undefined,
            },
          },
        }}
      />

      <VoucherWrapper />

      <LabelWrapper>
        <Label textAlign="center">
          {localize(LocalizationCategory.product, ProductLocalization.toBePaid)}:{' '}
          {bookingPrices.toBePaid ? formatPrice(bookingPrices.toBePaid) : '-'}
        </Label>
      </LabelWrapper>

      {errorMessage ? (
        <Label error mb="3">
          {errorMessage}
        </Label>
      ) : null}

      <ModalButton
        isLoading={loading}
        onClick={handlePayment}
        disabled={isPayButtonDisabled}
      >
        {localize(LocalizationCategory.product, ProductLocalization.confirmBooking)}
      </ModalButton>
    </StyledForm>
  );
};

export default PaymentForm;
